import type { MetaAttrs } from '@nuxtjs/i18n';
import lodashCompact from 'lodash-es/compact';
import type { Ref } from 'vue';

import { AssetType } from '~/apiClient';
import { FIRST_PAGE } from '~/constants/pagination';
import { defaultLocale, Locale } from '~i18n/i18nConfig';

type UseSlpSeoMetaProps = {
    totalItems: Ref<number | undefined>;
};

export const useSlpSeoMeta = ({ totalItems }: UseSlpSeoMetaProps): void => {
    // COMPOSABLES
    const { tt } = useTypedI18n();
    const route = useRoute();
    const sellingMethod = useSlpSellingMethod();
    const parsedRouteParams = useSlpParsedRouteParamsState(route);
    const { brandName, brandUrlKeys, modelName, page } = useSlpParams(sellingMethod);
    const { frontendBaseUrl } = useRuntimeConfig().public;
    const { getSlpLocalizedPath } = useSlpRouteUtils();
    const i18nHead = useLocaleHead({
        seo:
            page.value === FIRST_PAGE // Preserve page parameter in canonical only if the page is != 1
                ? true
                : {
                      canonicalQueries: ['page'],
                  },
    });

    // DATA
    const assetType = computed<string>(() => tt(`asset.type.${parsedRouteParams.value.assetType as AssetType}`, 2)); // Plural form in ALP
    const platformClass = computed<string>(() =>
        parsedRouteParams.value.platformClass ? tt(`asset.class.${parsedRouteParams.value.platformClass}`) : ''
    );
    const titleParams = computed<string[]>(() => [lodashCompact([assetType.value, platformClass.value, brandName.value, modelName.value]).join(' ')]);
    const descriptionParams = computed<string[]>(() => [
        lodashCompact([assetType.value, brandName.value, modelName.value]).join(' '),
        lodashCompact([assetType.value, platformClass.value]).join(' '),
    ]);

    // A multi-brand listing has no canonical of its own: `canonicalQueries` keeps `page` only, so the canonical would point at the single brand
    // sitting in the path, i.e. at a different result set. Only single-brand path urls are advertised in the sitemap, so keep combinations out.
    const robots = computed<string | undefined>(() => {
        if (!totalItems.value) {
            return 'noindex, nofollow';
        }

        return brandUrlKeys.value.length > 1 ? 'noindex, follow' : undefined;
    });

    // SEO
    const excludeAlternateLinks = (l: MetaAttrs): boolean => l.rel !== 'alternate';

    const mapOgUrl = (l: MetaAttrs): MetaAttrs => {
        if (l.property !== 'og:url') {
            return l;
        }

        // Remap og url with the fullPath
        return { ...l, content: `${frontendBaseUrl}${route.fullPath}` };
    };

    const alternateLinks = Object.values(Locale).map(locale => {
        const id = `i18n-alt-${locale}`;
        const path = getSlpLocalizedPath(locale);
        const href = `${frontendBaseUrl}${path}`;

        return { id, rel: 'alternate', href, hreflang: locale };
    });

    // Ids match the ones the i18n module assigns, so these links override the unlocalized set app.vue emits instead of duplicating it
    const xDefaultLink = {
        id: 'i18n-xd',
        rel: 'alternate',
        href: `${frontendBaseUrl}${getSlpLocalizedPath(defaultLocale)}`,
        hreflang: 'x-default',
    };

    useSeoMeta({
        title: () => {
            return tt(`seo.slp.${sellingMethod.value}.title`, titleParams.value);
        },
        ogTitle: tt(`seo.slp.${sellingMethod.value}.title`, titleParams.value),
        description: tt(`seo.slp.${sellingMethod.value}.description`, descriptionParams.value),
        ogDescription: tt(`seo.slp.${sellingMethod.value}.description`, descriptionParams.value),
        robots,
    });

    useHead({
        link: [...i18nHead.value.link.filter(excludeAlternateLinks), xDefaultLink, ...alternateLinks],
        meta: i18nHead.value.meta.map(mapOgUrl),
    });
};
